import React, { useState } from "react";
import { toast } from "react-toastify";
import {
  ActiveFilterChips,
  DataTable,
  InlineFilters,
  DateCell,
  ListFilters,
  ListSearchForm,
  SearchToolbar,
  TablePagination,
  formatResultCount,
  useListPage,
} from "../../common/DataList";
import usePersistedState from "../../Hooks/usePersistedState/usePersistedState";
import dataService from "../../../helpers/dataService";
import tools from "../../../helpers/tools";
import { sidePanelActions } from "../../../context/actions/sidePanelActions";
import { modalActions } from "../../../context/actions/modalActions";
import useStore from "../../../context/useStore";
import StatutDeclarationBadge from "./components/StatutDeclarationBadge";
import DetailDeclarationSanctionCAF, {
  libelleDecisionEP,
  tonLigneDeclaration,
} from "./components/DetailDeclarationSanctionCAF";
import { estAnonymise } from "../../../helpers/allocataire";
import {
  BoutonRelancer,
  BoutonVoir,
  CelluleAllocataire,
  Note,
  libelleBilanRelance,
  nomAllocataire,
  ouvrirConfirmationRelanceEnMasse,
} from "./components/blocsSanctionFT";
import {
  DECLARATIONS_DEFAULT_FILTERS,
  DECLARATIONS_ENDPOINT,
  DECLARATIONS_FILTER_DEFINITIONS,
  DECLARATIONS_FILTER_GROUPS,
  DECLARATIONS_PERSIST_KEY,
  RELANCER_DECLARATIONS_EN_ECHEC_ENDPOINT,
  buildDeclarationsPayload,
  declarationsAtoms,
  relancerDeclarationEndpoint,
} from "./sanctionsFTListConfig";

export const FILTERS_PANEL_ID = "declarations-caf-filters";
const FILTERS_OPEN_KEY = "espoar.boa.sanctionsFT.declarations.filtersOpen";

const COUNT_LABELS = {
  none: "Aucune sanction déclarée",
  one: "sanction déclarée",
  many: "sanctions déclarées",
};

export const CelluleDecisionEP = ({ declaration: el }) =>
  el.decisionEP ? (
    <>
      <span className="cell-primary">{libelleDecisionEP(el.decisionEP)}</span>
      <Note>{el.decisionEP.motifLabel}</Note>
      {el.decisionEP.dateFinalisationEP && (
        <Note>EP finalisée le {tools.formatDate(el.decisionEP.dateFinalisationEP)}</Note>
      )}
    </>
  ) : (
    <span className="text-muted">Inscription introuvable</span>
  );

export const CelluleEtatDeclaration = ({ declaration: el }) => (
  <>
    <StatutDeclarationBadge statut={el.statut} statutLabel={el.statutLabel} />
    {el.radiation && (
      <Note
        ton={el.radiation.effectuee ? "ok" : "warn"}
        icon={el.radiation.effectuee ? "user-times" : "exclamation-circle"}
      >
        Radiation : {el.radiation.label}
      </Note>
    )}
    {el.radiationDE && !el.radiation && (
      <Note ton="warn" icon="hourglass-half">
        Radiation prononcée, pas encore transmise
      </Note>
    )}
    {el.blocage && (
      <Note ton="ko" icon="exclamation-triangle">
        Dernière tentative en échec
      </Note>
    )}
    {el.raisonNonDeclarable && <Note>{el.raisonNonDeclarable}</Note>}
  </>
);

export const getDeclarationsColumns = ({ ouvrirDetail, relancer = null, relances = [] }) => [
  {
    key: "dateCreation",
    header: "Déclarée le",
    className: "col-date",
    render: (el) => <DateCell value={el.dateCreation} />,
  },
  {
    key: "allocataire",
    header: "Allocataire",
    className: "col-recipient",
    render: (el) => (
      <CelluleAllocataire
        allocataire={el.allocataire}
        secondaire={
          el.identifiantConsequenceSanction
            ? `Conséquence FT ${el.identifiantConsequenceSanction}`
            : null
        }
      />
    ),
  },
  {
    key: "decisionEP",
    header: "Décision de l'EP",
    render: (el) => <CelluleDecisionEP declaration={el} />,
  },
  {
    key: "etat",
    header: "État",
    render: (el) => <CelluleEtatDeclaration declaration={el} />,
  },
  {
    key: "actions",
    header: "Actions",
    headerSrOnly: true,
    className: "col-actions",
    render: (el) => (
      <>
        <BoutonVoir
          className="declarations-caf__voir"
          onClick={() => ouvrirDetail(el)}
          label={`Voir la déclaration${
            nomAllocataire(el.allocataire) ? ` de ${nomAllocataire(el.allocataire)}` : ""
          }`}
        />
        {relancer && el.relancable && !estAnonymise(el.allocataire) && (
          <BoutonRelancer
            className="declarations-caf__relancer"
            onClick={() => relancer(el.inscriptionEPId)}
            enCours={relances.includes(el.inscriptionEPId)}
            alerte={Boolean(el.blocage)}
            label={`Relancer la déclaration${
              nomAllocataire(el.allocataire) ? ` de ${nomAllocataire(el.allocataire)}` : ""
            }`}
          />
        )}
      </>
    ),
  },
];

const DeclarationsSanctionCAFList = () => {
  const [state, dispatch] = useStore();
  const spActions = sidePanelActions(state, dispatch);
  const mActions = modalActions(state, dispatch);
  const [filtersOpen, setFiltersOpen] = usePersistedState(FILTERS_OPEN_KEY, true);
  const [relances, setRelances] = useState([]);
  const [relanceEnMasseEnCours, setRelanceEnMasseEnCours] = useState(false);

  const list = useListPage({
    searchAtom: declarationsAtoms.searchAtom,
    resultsAtom: declarationsAtoms.resultsAtom,
    defaultFilters: DECLARATIONS_DEFAULT_FILTERS,
    endpoint: DECLARATIONS_ENDPOINT,
    buildPayload: buildDeclarationsPayload,
    persistKey: DECLARATIONS_PERSIST_KEY,
    definitions: DECLARATIONS_FILTER_DEFINITIONS,
    groups: DECLARATIONS_FILTER_GROUPS,
  });

  // La correction se fait ailleurs — une donnée d'identité à compléter, France
  // Travail à attendre. Ce bouton ne fait que redemander l'envoi sans attendre
  // le passage du cron.
  function relancer(inscriptionEPId) {
    setRelances((ids) => [...ids, inscriptionEPId]);
    dataService.post(
      relancerDeclarationEndpoint(inscriptionEPId),
      {},
      () => {
        toast.success("Envoi remis en file : son issue apparaîtra ici dans quelques instants.");
        list.runSearch(list.search);
      },
      (erreur) => toast.error(erreur.other || "La relance a échoué"),
      () => setRelances((ids) => ids.filter((id) => id !== inscriptionEPId))
    );
  }

  // Confirmation obligatoire, dans la modale du back-office : la relance porte
  // sur tous les envois en échec, pas sur la page affichée, et peut déclencher
  // des centaines d'appels.
  function relancerToutEnEchec() {
    ouvrirConfirmationRelanceEnMasse(mActions, {
      phrase: "Tous les envois de déclaration en échec seront remis en file",
      onConfirmer: lancerRelanceEnMasse,
    });
  }

  function lancerRelanceEnMasse() {
    setRelanceEnMasseEnCours(true);
    dataService.post(
      RELANCER_DECLARATIONS_EN_ECHEC_ENDPOINT,
      {},
      (bilan) => {
        toast.success(libelleBilanRelance(bilan, "déclaration"));
        list.runSearch(list.search);
      },
      (erreur) => toast.error(erreur.other || "La relance en masse a échoué"),
      () => setRelanceEnMasseEnCours(false)
    );
  }

  function ouvrirDetail(declaration) {
    spActions.updateSidePanel({
      isOpen: true,
      content: (
        <DetailDeclarationSanctionCAF
          declaration={declaration}
          enCoursDeRelance={relances.includes(declaration.inscriptionEPId)}
          onRelancer={(id) => {
            spActions.updateSidePanel({ isOpen: false, content: null });
            relancer(id);
          }}
        />
      ),
    });
  }

  const columns = getDeclarationsColumns({ ouvrirDetail, relancer, relances });
  const aDesEchecs = list.items.some((el) => el.blocage);

  return (
    <div className="list-page declarations-caf">
      <ListSearchForm onSubmit={list.submit} aria-label="Recherche de sanctions déclarées">
        <SearchToolbar
          id="declarations-caf-search"
          name="terms"
          value={list.filters.terms}
          onChange={list.onFilterChange}
          placeholder="Identifiant de conséquence France Travail"
          isLoading={list.isLoading}
          isDirty={list.isDirty}
          inlineFilters={
            <InlineFilters
              definitions={list.principalFilters}
              filters={list.filters}
              onChange={list.onFilterChange}
              onSet={list.setFilter}
            />
          }
          inlineFiltersLayout="ligne"
          filtersLabel="Plus de filtres"
          filtersPanelId={FILTERS_PANEL_ID}
          filtersOpen={filtersOpen}
          onToggleFilters={list.hasPanelFilters ? () => setFiltersOpen((open) => !open) : null}
          activeFiltersCount={list.panelActiveFiltersCount}
        />
        <ActiveFilterChips chips={list.chips} onRemove={list.removeFilter} onClear={list.resetFilters} />
        {list.hasPanelFilters && (
          <ListFilters
            id={FILTERS_PANEL_ID}
            open={filtersOpen}
            groups={list.groups}
            filters={list.filters}
            onChange={list.onFilterChange}
            onSet={list.setFilter}
            onReset={list.resetFilters}
            canReset={list.canReset}
          />
        )}
      </ListSearchForm>

      <section className="list-card" aria-label="Résultats">
        <div className="results-bar">
          <p className="results-bar__count" role="status" aria-live="polite">
            {formatResultCount(list.count, COUNT_LABELS)}
          </p>
          {aDesEchecs && (
            <div className="results-bar__actions">
              <button
                type="button"
                className="btn btn-sm btn-warning declarations-caf__relancer-tout"
                onClick={relancerToutEnEchec}
                disabled={relanceEnMasseEnCours}
                aria-busy={relanceEnMasseEnCours ? true : undefined}
                title="Remet en file tous les envois en échec, pas seulement ceux de cette page"
              >
                <i className={`fa mr-1 ${relanceEnMasseEnCours ? "fa-spinner fa-spin" : "fa-redo"}`} aria-hidden="true" />
                Relancer tout ce qui est en échec
              </button>
            </div>
          )}
        </div>
        <DataTable
          caption="Sanctions déclarées par la CAF à France Travail"
          columns={columns}
          rows={list.items}
          rowKey={(el, index) => el.id || el.inscriptionEPId || index}
          rowClassName={(el) =>
            tonLigneDeclaration(el) ? `row--${tonLigneDeclaration(el)}` : ""
          }
          isLoading={list.isLoading}
          hasSearched={list.hasSearched}
          error={list.error}
          emptyState={{
            title: "Aucune sanction déclarée ne correspond à ces critères",
            hint: "Modifiez votre recherche ou vos filtres.",
            action: list.canReset
              ? { label: "Réinitialiser les filtres", onClick: list.resetFilters }
              : null,
          }}
        />
        <TablePagination
          page={list.page}
          nbPages={list.nbPages}
          count={list.count}
          nbItemsByPage={list.nbItemsByPage}
          onChangePage={list.changePage}
          onChangeNbItemsByPage={list.changeNbItemsByPage}
          itemLabel="sanctions déclarées"
        />
      </section>
    </div>
  );
};

export default DeclarationsSanctionCAFList;
